import { useState, useEffect } from "react";
import {
  Calendar,
  Heart,
  Package,
  Truck,
  ArrowUp,
  ArrowDown,
  FileText,
  Bell,
  ChevronDown,
  Users,
  Building,
  Activity,
  Clock,
  Pin
} from "lucide-react";
import {
  fetchTodaysCampaigns,
  fetchStats,
  fetchNotice,
  fetchOrganizations,
  fetchUpcomingCampaign,
  STORAGE_KEYS,
  debugAuthState,
  clearAuthData,
} from "../../services/api";
import Select from "react-select";

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color = "primary" }) => {
  const getColorClasses = (color) => {
    const colorMap = {
      primary: "bg-primary-100 text-primary-600",
      blue: "bg-blue-100 text-blue-600",
      green: "bg-green-100 text-green-600",
      yellow: "bg-yellow-100 text-yellow-600",
      purple: "bg-purple-100 text-purple-600",
      red: "bg-red-100 text-red-600",
      indigo: "bg-indigo-100 text-indigo-600",
    };
    return colorMap[color] || colorMap.primary;
  };

  return (
    <div className="card p-2 sm:p-4 md:p-6 hover:shadow-medium transition-shadow duration-200">
      <div className="flex items-center">
        <div className={`p-2 sm:p-3 rounded-lg ${getColorClasses(color)}`}>
          <Icon className="h-6 w-6" />
        </div>
        <div className="ml-2 sm:ml-4">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-xl sm:text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

// Notice Card Component
const NoticeCard = ({ notice }) => (
  <div className="card p-2 sm:p-4 md:p-6 hover:shadow-medium transition-shadow duration-200">
    <div className="flex items-start">
      <div className="p-2 rounded-lg bg-primary-100">
        <Bell className="h-5 w-5 text-primary-600" />
      </div>
      <div className="ml-2 sm:ml-4 flex-1">
        <h3 className="text-sm font-semibold text-gray-900 mb-1">
          {notice.name || "Untitled Notice"}
        </h3>
        <p className="text-sm text-gray-600">
          {notice.description || "No description available"}
        </p>
      </div>
    </div>
  </div>
);

// User Type Detection
const getUserTypeFlags = async () => {
  try {
    const bpartnerData = localStorage.getItem(STORAGE_KEYS.B_PARTNER);
    if (bpartnerData) {
      const data = JSON.parse(bpartnerData);
      return {
        isBDO: !!data.isbdo,
        isHospital: !!data.isHospital,
        isCollection: !!(data.IsEmployee || data.IsVolunteer),
        isPublicUser: !!(
          data.isdonor &&
          !data.isbdo &&
          !data.isHospital &&
          !(data.IsEmployee || data.IsVolunteer)
        ),
      };
    }
  } catch (err) {
    console.error("Error parsing bpartner data:", err);
  }
  return {
    isBDO: false,
    isHospital: false,
    isCollection: false,
    isPublicUser: false,
  };
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [noticeData, setNoticeData] = useState([]);
  const [upcomingCampaigns, setUpcomingCampaigns] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [orgLoading, setOrgLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOrgDropdownOpen, setIsOrgDropdownOpen] = useState(false);
  const [userType, setUserType] = useState({ isBDO: false, isHospital: false });

  useEffect(() => {
    // Debug authentication state
    debugAuthState();
    loadUserType();
    loadOrganizations();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOrgDropdownOpen && !event.target.closest(".org-dropdown")) {
        setIsOrgDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOrgDropdownOpen]);

  useEffect(() => {
    // Load dashboard data regardless of organization selection
    loadDashboardData();
    if (selectedOrgId) {
      loadNotices();
      loadUpcomingCampaigns();
    }
  }, [selectedOrgId]);

  const loadUserType = async () => {
    try {
      const userTypeFlags = await getUserTypeFlags();
      setUserType(userTypeFlags);
      // console.log("User type flags:", userTypeFlags);
    } catch (err) {
      console.error("Failed to load user type:", err);
    }
  };

  const loadOrganizations = async () => {
    try {
      setOrgLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

      if (!token) {
        console.log("No authentication token found");
        setOrgLoading(false);
        return;
      }

      const orgs = await fetchOrganizations();
      setOrganizations(orgs);

      const lastSelectedOrgId = localStorage.getItem(
        STORAGE_KEYS.LAST_SELECTED_ORG_ID
      );

      if (lastSelectedOrgId) {
        const foundOrg = orgs.find(
          (org) => String(org.id) === lastSelectedOrgId
        );
        if (foundOrg) {
          setSelectedOrgId(foundOrg.id);
        } else if (orgs.length > 0) {
          setSelectedOrgId(orgs[0].id);
          localStorage.setItem(
            STORAGE_KEYS.LAST_SELECTED_ORG_ID,
            String(orgs[0].id)
          );
        }
      } else if (orgs.length > 0) {
        setSelectedOrgId(orgs[0].id);
        localStorage.setItem(
          STORAGE_KEYS.LAST_SELECTED_ORG_ID,
          String(orgs[0].id)
        );
      }
    } catch (err) {
      console.error("Failed to fetch organizations:", err);
      setOrganizations([]);
    } finally {
      setOrgLoading(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      // console.log("Loading dashboard data...");
      const [campaignData, statsData] = await Promise.all([
        fetchTodaysCampaigns(),
        fetchStats(),
      ]);
      setStats(statsData);
      // console.log("Fetched Dashboard Stats:", statsData);
      setError(null);
    } catch (err) {
      console.error("Dashboard data loading error:", err);
      // Set default stats if API fails
      setStats({
        campsConducted: 0,
        completedDonations: 0,
        pendingBloodRequests: 0,
        completedBloodRequests: 0,
        bloodShipments: 0,
        issuedBloods: 0,
        usedBloods: 0,
        invoices: 0,
      });
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const loadNotices = async () => {
    if (selectedOrgId) {
      try {
        // console.log("Loading notices for organization ID:", selectedOrgId);
        const notices = await fetchNotice(selectedOrgId);
        // console.log("Loaded notices:", notices);
        // console.log("Notice data type:", typeof notices);
        // console.log("Notice data length:", notices ? notices.length : 0);
        setNoticeData(notices || []);
      } catch (err) {
        console.error("Failed to fetch notices:", err);
        setNoticeData([]);
      }
    } else {
      console.log("No organization selected, skipping notice load");
    }
  };

  const loadUpcomingCampaigns = async () => {
    if (selectedOrgId) {
      try {
        console.log(
          "Loading upcoming campaigns for organization ID:",
          selectedOrgId
        );
        const campaigns = await fetchUpcomingCampaign(selectedOrgId);
        console.log("Loaded upcoming campaigns:", campaigns);

        // Handle different response formats and filter valid campaigns
        let campaignsArray = [];
        if (Array.isArray(campaigns)) {
          campaignsArray = campaigns;
        } else if (campaigns && typeof campaigns === "object") {
          campaignsArray = campaigns.records || Object.values(campaigns);
        }

        // Filter out any invalid campaigns - campaigns should have at least a venue or startdate
        const validCampaigns = campaignsArray.filter(
          (campaign) => campaign && (campaign.venue || campaign.startdate)
        );

        setUpcomingCampaigns(validCampaigns || []);
      } catch (err) {
        console.error("Failed to fetch upcoming campaigns:", err);
        setUpcomingCampaigns([]);
      }
    } else {
      console.log("No organization selected, skipping upcoming campaigns load");
    }
  };

  const handleOrgChange = async (orgId) => {
    setSelectedOrgId(orgId);
    try {
      localStorage.setItem(STORAGE_KEYS.LAST_SELECTED_ORG_ID, String(orgId));
      console.log("Saved selected organization ID:", orgId);
    } catch (e) {
      console.error("Failed to save selected organization ID", e);
    }
  };

  if (loading && orgLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button onClick={loadDashboardData} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Check if user is authenticated
  const token = localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);

  if (!token) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="bg-primary-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Bell className="h-8 w-8 text-primary-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Required
          </h2>
          <p className="text-gray-600 mb-4">
            Please log in to access the dashboard
          </p>
          <div className="space-y-3">
            <button
              onClick={() => (window.location.href = "/login")}
              className="btn-primary"
            >
              Go to Login
            </button>
            <button
              onClick={() => {
                clearAuthData();
                window.location.reload();
              }}
              className="btn-secondary ml-2"
            >
              Clear Auth Data
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Check if user type is allowed on web platform
  if (userType.isCollection || userType.isPublicUser) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="bg-red-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <Users className="h-8 w-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Only Hospital and Blood Donation Organization users can access the
            web platform.
          </p>
          <button
            onClick={() => {
              clearAuthData();
              window.location.href = "/login";
            }}
            className="btn-primary"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <div className="flex flex-col sm:flex-row items-start sm:items-center mb-2">
          {userType.isHospital ? (
            <Building className="h-6 w-6 text-red-600 mr-2" />
          ) : userType.isBDO ? (
            <Activity className="h-6 w-6 text-green-600 mr-2" />
          ) : null}
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {userType.isHospital
              ? "Hospital Dashboard"
              : userType.isBDO
              ? "BDO Dashboard"
              : "Dashboard"}
          </h1>
        </div>
        <p className="text-gray-600 text-sm sm:text-base">
          Welcome to your BTSM{" "}
          {userType.isHospital
            ? "Hospital"
            : userType.isBDO
            ? "Blood Donation Organization"
            : ""}{" "}
          dashboard
        </p>
      </div>

      {/* Organization Selector */}
      <div className="card p-2 sm:p-4 md:p-6">
        <label className="block text-sm font-medium text-red-600 mb-3">
          Select BTS Chapter
        </label>
        {orgLoading ? (
          <div className="flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500 mr-3"></div>
            <span className="text-gray-600">Loading chapters...</span>
          </div>
        ) : (
          <Select
            value={
              organizations.find((org) => org.id === selectedOrgId)
                ? {
                    value: selectedOrgId,
                    label: organizations.find((org) => org.id === selectedOrgId)
                      ?.Name,
                  }
                : null
            }
            onChange={(option) => handleOrgChange(option ? option.value : "")}
            options={organizations.map((org) => ({
              value: org.id,
              label: org.Name,
            }))}
            placeholder="Select Chapter"
            classNamePrefix="react-select"
            isClearable={false}
            isSearchable={true}
          />
        )}
      </div>

      {/* Notices Section */}
      <div className="card p-2 sm:p-4 md:p-6">
        <h2 className="text-lg font-semibold text-red-600 mb-4">Notices</h2>
        {/* {console.log("Rendering notices section, noticeData:", noticeData)} */}
        {/* {console.log("NoticeData length:", noticeData ? noticeData.length : 0)} */}
        {noticeData && noticeData.length > 0 ? (
          <div className="space-y-4">
            {noticeData.map((notice, index) => (
              <NoticeCard key={index} notice={notice} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 font-medium">No Notices Available</p>
          </div>
        )}
      </div>

      {/* Upcoming Campaigns Section */}
      <div className="card p-2 sm:p-4 md:p-6">
        <h2 className="text-lg font-semibold text-red-600 mb-4">
          Upcoming Campaign
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {upcomingCampaigns && upcomingCampaigns.length > 0 ? (
            upcomingCampaigns.map((campaign, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-200 p-3"
              >
                {/* Header with status */}
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {/* <div className="w-2 h-2 rounded-full bg-red-500"></div>
                     */}
                    <Pin className="w-4 h-4 text-gray-400" />
                    <h3 className="font-semibold text-gray-900 text-sm truncate">
                      {campaign.venue && campaign.venue !== "-"
                        ? `${campaign.venue}`
                        : "Blood Donation Campaign"}
                    </h3>
                  </div>
                  <span
                    className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${
                      campaign.docstatus === "C"
                        ? "bg-green-100 text-green-800"
                        : campaign.docstatus === "R"
                        ? "bg-blue-100 text-blue-800"
                        : "bg-gray-100 text-gray-800"
                    }`}
                  >
                    {campaign.docstatus === "C"
                      ? "Confirmed"
                      : campaign.docstatus === "R"
                      ? "Running"
                      : "Pending"}
                  </span>
                </div>

                {/* Content in vertical stack for better fit */}
                <div className="space-y-2 text-sm">
                  {/* Date */}
                  <div className="flex items-center space-x-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">
                      {campaign.startdate
                        ? new Date(campaign.startdate).toLocaleDateString(
                            "en-US",
                            {
                              month: "short",
                              day: "numeric",
                              year: "numeric",
                            }
                          )
                        : "TBD"}
                    </span>
                  </div>

                  {/* Time */}
                  <div className="flex items-center space-x-2">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-900 font-medium">
                      {campaign.starttime && campaign.starttime !== "00:01:00"
                        ? campaign.starttime
                        : "TBD"}
                    </span>
                  </div>

                  {/* Venue */}
                  {/* <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-gray-400" />
                    <span className="text-gray-600 truncate">
                      {campaign.venue && campaign.venue !== "-"
                        ? campaign.venue
                        : "TBD"}
                    </span>
                  </div> */}
                </div>
              </div>
            ))
          ) : (
            <div className="p-4 border border-gray-100 rounded-lg text-center">
              <p className="text-gray-500 font-medium">
                No Upcoming Campaigns Available
              </p>
              <p className="text-sm text-gray-400 mt-1">
                Please check back later for updates.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
        {/* BDO users: Show only Camps Conducted and Completed Donations */}
        {userType.isBDO && (
          <>
            <StatsCard
              title="Camps Conducted"
              value={stats?.campsConducted || 0}
              icon={Calendar}
              color="blue"
            />
            <StatsCard
              title="Completed Donations"
              value={stats?.completedDonations || 0}
              icon={Heart}
              color="green"
            />
          </>
        )}

        {/* Hospital users: Show all cards except Camps Conducted and Completed Donations */}
        {userType.isHospital && (
          <>
            <StatsCard
              title="Pending Requests"
              value={stats?.pendingBloodRequests || 0}
              icon={Package}
              color="yellow"
            />
            <StatsCard
              title="Completed Requests"
              value={stats?.completedBloodRequests || 0}
              icon={Package}
              color="green"
            />
            <StatsCard
              title="Blood Shipments"
              value={stats?.bloodShipments || 0}
              icon={Truck}
              color="purple"
            />
            <StatsCard
              title="Issued Blood"
              value={stats?.issuedBloods || 0}
              icon={ArrowUp}
              color="blue"
            />
            <StatsCard
              title="Used Blood"
              value={stats?.usedBloods || 0}
              icon={ArrowDown}
              color="red"
            />
            <StatsCard
              title="Invoices"
              value={stats?.invoices || 0}
              icon={FileText}
              color="indigo"
            />
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
